import type {
  Equation,
  EquationDocument,
  EquationEvaluation,
  EquationEvaluationState,
  EvaluationResult,
  MatrixNode,
  VectorNode,
} from "./equation";
import type { Dimension, MatrixFor, VectorFor } from "@src/math/types";
import { MAX_EQUATION_NODES, matrixDurationMs } from "./equation-policy";
import {
  draftMatrixValues,
  draftVectorValues,
  expressionDraft,
  matrixLabel,
  nodeId,
  vectorColor,
  vectorLabel,
} from "./equation-values";
import { identity, vector as toVector } from "@src/math/matrix";

export function editEquation<D extends Dimension, Result>(
  equation: Equation<D>,
  edit: (draft: Equation<D>) => Result,
): { readonly equation: Equation<D>; readonly result: Result } {
  const draft = {
    ...equation,
    matrices: equation.matrices.map((matrix) => ({
      ...matrix,
      entries: draftMatrixValues(equation.dimension, matrix.entries),
    })),
    vectors: equation.vectors.map((vector) => ({
      ...vector,
      coordinates: draftVectorValues(equation.dimension, vector.coordinates),
    })),
  };
  return { equation: draft, result: edit(draft) };
}

let localIdSequence = 0;

function createLocalId(): string {
  localIdSequence += 1;
  return `node-${String(localIdSequence)}`;
}

export function createEquation<D extends Dimension>(
  dimension: D,
  createId: () => string = createLocalId,
): Equation<D> {
  const matrixId = createId();
  const vectorId = createId();
  if (matrixId === vectorId)
    throw new Error("Equation node IDs must be unique");
  const matrix = createMatrixNode(
    dimension,
    "A",
    undefined,
    undefined,
    matrixId,
  );
  const vector = createVectorNode(
    dimension,
    "v1",
    "#f4b740",
    undefined,
    undefined,
    vectorId,
  );
  const totalTransform = identity(dimension);
  const standardBasis = Array.from({ length: dimension }, (_, column) =>
    toVector(
      dimension,
      Array.from({ length: dimension }, (_, row) => (row === column ? 1 : 0)),
    ),
  );
  return {
    dimension,
    matrices: [matrix],
    vectors: [vector],
    evaluation: {
      kind: "current",
      matrixAnalysis: {
        inverseTransform: {
          kind: "inverse",
          values: identity(dimension),
        },
        eigensystem: {
          kind: "available",
          spectrum: "real",
          realEigenvectors: standardBasis,
        },
      },
      value: {
        dimension,
        matrices: [
          {
            id: matrix.id,
            label: matrix.label,
            values: identity(dimension),
            durationMs: matrix.durationMs,
          },
        ],
        vectors: [
          {
            id: vector.id,
            label: vector.label,
            coordinates: toVector(dimension, [1, 1, 1].slice(0, dimension)),
            color: vector.color,
          },
        ],
        totalTransform,
      },
    },
  };
}

export function createMatrixNode<D extends Dimension>(
  dimension: D,
  label: string,
  values?: MatrixFor<D>,
  draftValues?: readonly string[],
  id = createLocalId(),
): MatrixNode<D> {
  const nextValues = values ?? identity(dimension);
  assertNumericShape(nextValues, dimension ** 2);
  const entries = draftMatrixValues(
    dimension,
    draftValues ?? Array.from(nextValues, String),
  );
  entries.forEach(expressionDraft);
  return {
    id: nodeId(id),
    label: matrixLabel(label),
    entries,
    durationMs: matrixDurationMs(900),
  };
}

export function createVectorNode<D extends Dimension>(
  dimension: D,
  label: string,
  color: string,
  components?: VectorFor<D>,
  draftComponents?: readonly string[],
  id = createLocalId(),
): VectorNode<D> {
  const nextComponents =
    components ?? toVector(dimension, [1, 1, 1].slice(0, dimension));
  assertNumericShape(nextComponents, dimension);
  const coordinates = draftVectorValues(
    dimension,
    draftComponents ?? Array.from(nextComponents, String),
  );
  coordinates.forEach(expressionDraft);
  return {
    id: nodeId(id),
    label: vectorLabel(label),
    coordinates,
    color: vectorColor(color),
  };
}

export function canAddEquationNodes(
  equation: {
    readonly matrices: readonly unknown[];
    readonly vectors: readonly unknown[];
  },
  kind: "matrices" | "vectors",
  count = 1,
): boolean {
  return (
    Number.isInteger(count) &&
    count >= 0 &&
    equation[kind].length + count <= MAX_EQUATION_NODES
  );
}

export function createRestoredEquation<D extends Dimension>(
  document: EquationDocument<D>,
  result: EvaluationResult<NoInfer<D>>,
  fallbackEvaluation:
    | EquationEvaluation<NoInfer<D>>
    | (() => EquationEvaluation<NoInfer<D>>),
): Equation<D> {
  return {
    dimension: document.dimension,
    matrices: document.matrices.map((matrix) => ({
      ...matrix,
      entries: draftMatrixValues(document.dimension, matrix.entries),
    })),
    vectors: document.vectors.map((vector) => ({
      ...vector,
      coordinates: draftVectorValues(document.dimension, vector.coordinates),
    })),
    evaluation: evaluationStateFromResult(result, fallbackEvaluation),
  };
}

export function evaluationStateFromResult<D extends Dimension>(
  result: EvaluationResult<D>,
  fallback: EquationEvaluation<D> | (() => EquationEvaluation<D>),
): EquationEvaluationState<D> {
  if (result.kind === "current") return result;
  const value = typeof fallback === "function" ? fallback() : fallback;
  return {
    kind: "stale",
    diagnostics: result.diagnostics,
    value,
    ...(result.checkpoint ? { checkpoint: result.checkpoint } : {}),
  };
}

function assertNumericShape(values: readonly number[], length: number): void {
  if (values.length !== length || !values.every(Number.isFinite))
    throw new Error("Invalid numeric values");
}
